import { inject, Injectable } from '@angular/core';
import { NewAccountApiService } from '../services/new-account-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  newAccountPageActions,
  newAccountPageAPIActions,
  newBulkAccountAPIActions,
  newBulkAccountPageActions,
  previousBulkImportsAPIActions,
  previousBulkImportsPageActions,
  previousImportAPIActions,
  previousImportPageActions
} from './new-accounts.actions';
import { catchError, map, switchMap, of, withLatestFrom, delay } from 'rxjs';
import { getPreviousImportsState, getSelectedPreviousImport } from './new-accounts.index';
import { Store } from '@ngrx/store';
import { DateService } from 'src/app/shared/services/date/date.service';

@Injectable()
export class NewAccountEffects {
  private actions$ = inject(Actions);
  private newAccountApiService = inject(NewAccountApiService);
  private router = inject(Router);
  private store = inject(Store);
  private dateService = inject(DateService);

  private _snackBar = inject(MatSnackBar);

  userSubmittedNewAccount = createEffect(() =>
    this.actions$.pipe(
      ofType(newAccountPageActions.userSubmittedNewAccount),
      switchMap((action) =>
        this.newAccountApiService.createNewAccount(action.newAccountData).pipe(
          map((response) => {
            if (response.isSuccess) {
              if (action.redirect) {
                this.router.navigate(['/account', response.result.accountId]);
              }
              this.openSnackBar('New customer account created successfully');
              return newAccountPageAPIActions.createNewAccountSuccess();
            } else {
              this.openSnackBar('Failed to create new customer account, Please try again.');
              return newAccountPageAPIActions.createNewAccountFailure({
                error: 'Failed to create new account'
              });
            }
          }),
          catchError((error) => {
            let errorMessage = 'Failed to create new customer account. Please try again.';
            const errors = error?.error?.errors ?? {};
            const backendMessage = errors['WordpressAccountUpsertFailed'];

            if (backendMessage?.includes('WordpressAccountUpsertFailed')) {
              errorMessage = 'This account was created in ID, but failed in Wordpress.';
            }

            this.openSnackBar(errorMessage);

            return of(
              newAccountPageAPIActions.createNewAccountFailure({
                error: backendMessage || errorMessage
              })
            );
          })
        )
      )
    )
  );

  importBulkAccounts = createEffect(() =>
    this.actions$.pipe(
      ofType(newBulkAccountPageActions.userSubmittedBulkAccount),
      switchMap((action) =>
        this.newAccountApiService.uploadBulkAccounts(action.bulkAccountData).pipe(
          map((response) => {
            if (response.isSuccess) {
              return newBulkAccountAPIActions.createBulkAccountSuccess({ eventId: response.result.eventId });
            } else {
              this.openSnackBar('Failed to create new customer accounts, Please try again.');
              return newBulkAccountAPIActions.createBulkAccountFailure({
                error: 'Failed to create new customer accounts'
              });
            }
          }),
          catchError((error) => {
            this.openSnackBar(error?.error?.message || 'Failed to create new customer accounts. Please try again.');

            return of(
              newBulkAccountAPIActions.createBulkAccountFailure({
                error: error?.error?.message || 'Failed to create new customer accounts. Please try again.'
              })
            );
          })
        )
      )
    )
  );

  getImportedAccountsStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(newBulkAccountAPIActions.createBulkAccountSuccess, newBulkAccountAPIActions.statusPending),
      switchMap((action) =>
        this.newAccountApiService.getBulkAccountsStatus(action.eventId).pipe(
          switchMap((response) => {
            if (response.pendingItems > 0) {
              return of(
                newBulkAccountAPIActions.statusPending({
                  eventId: action.eventId,
                  status: response
                })
              ).pipe(delay(3000)); // Wait 3 seconds before polling again
            } else {
              return of(
                newBulkAccountAPIActions.statusSuccess({
                  status: response
                })
              );
            }
          }),
          catchError((error) => {
            this.openSnackBar(error?.error?.message ?? 'Failed to retrieve bulk account status. Please try again.');

            return of(
              newBulkAccountAPIActions.statusFailure({
                error: error?.error?.message ?? 'Failed to retrieve bulk account status. Please try again.'
              })
            );
          })
        )
      )
    )
  );

  loadPreviousImports = createEffect(() =>
    this.actions$.pipe(
      ofType(previousBulkImportsPageActions.userRequestedPreviousImports, previousBulkImportsPageActions.userRequestedMorePreviousImports),
      withLatestFrom(this.store.select(getPreviousImportsState)),
      switchMap(([, previousImportsState]) =>
        this.newAccountApiService
          .getPreviousBulkImports(previousImportsState.pageNumber, previousImportsState.pageSize)
          .pipe(
            map((response) => {
              if (response.items) {
                return previousBulkImportsAPIActions.getPreviousImportsSuccess({
                  previousImports: response.items,
                  totalCount: response.totalCount
                });
              } else {
                this.openSnackBar('Failed to load previous imports, Please try again.');
                return previousBulkImportsAPIActions.getPreviousImportsFailure({
                  error: 'Failed to load previous imports'
                });
              }
            }),
            catchError((error) => {
              this.openSnackBar(error?.error?.message || 'Failed to load previous imports. Please try again.');

              return of(
                previousBulkImportsAPIActions.getPreviousImportsFailure({
                  error: error?.error?.message || 'Failed to load previous imports. Please try again.'
                })
              );
            })
          )
      )
    )
  );

  downloadPreviousImportFile = createEffect(() =>
    this.actions$.pipe(
      ofType(previousBulkImportsPageActions.userRequestedDownloadImportFile),
      switchMap((action) =>
        this.newAccountApiService.getPreviousBulkImportFile(action.eventId).pipe(
          map((response) => {
            if (response) {
              const blob = new Blob([response], {
                type: 'text/csv;charset=utf-8;'
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Nessy_BulkImport_${this.dateService.formatToLocalTime(action.createdAt)}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);

              return previousBulkImportsAPIActions.downloadImportFileSuccess();
            } else {
              this.openSnackBar('Failed to download previous import documents, Please try again.');
              return previousBulkImportsAPIActions.downloadImportFileFailure({
                error: 'Failed to download previous import documents'
              });
            }
          }),
          catchError(() =>
            of(
              previousBulkImportsAPIActions.downloadImportFileFailure({
                error: 'Failed to download previous import documents'
              })
            )
          )
        )
      )
    )
  );

   loadSelectedBulkUploadDetail = createEffect(() =>
    this.actions$.pipe(
      ofType(previousImportPageActions.userRequestedPreviousImportDetail, previousImportPageActions.userRequestedMorePreviousImportDetails),
      withLatestFrom(this.store.select(getSelectedPreviousImport)),
      switchMap(([, state]) =>
        this.newAccountApiService.getSelectedBulkImportDetail(state.id, state.pageSize).pipe(
          map((response) => {
            if (response) {
              return previousImportAPIActions.getPreviousImportDetailSuccess({ previousImportDetail: response });
            } else {
              this.openSnackBar('Failed to load previous import detail, Please try again.');
              return previousImportAPIActions.getPreviousImportDetailFailure({
                error: 'Failed to load previous import detail'
              });
            }
          }),
          catchError((error) => {
            this.openSnackBar(error?.error?.message || 'Failed to load previous import detail. Please try again.');

            return of(
              previousImportAPIActions.getPreviousImportDetailFailure({
                error: error?.error?.message || 'Failed to load previous import detail. Please try again.'
              })
            );
          })
        )
      )
    )
  );

  openSnackBar(message: string) {
    this._snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right'
    });
  }
}
